import { useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { GroupChannelHandler } from "@sendbird/chat/groupChannel";
import {
  sendbirdSelectors,
  useSendbirdStateContext,
} from "@sendbird/uikit-react";
import { notifyIncomingSendbirdMessage } from "Messenger/hooks/notifyIncomingSendbirdMessage";

/**
 * Shows an OS notification for incoming Sendbird messages while the page is
 * loaded (focused team chat, other pages, or a background tab).
 * Closed-app delivery still depends on FCM + the messaging service worker.
 */
const useSendbirdDesktopNotifications = () => {
  const context = useSendbirdStateContext();
  const sdk = sendbirdSelectors.getSdk(context);
  const isSdkInitialized = Boolean(context?.stores?.sdkStore?.initialized);
  const handlerIdRef = useRef("");
  const currentUserId = sdk?.currentUser?.userId;

  useEffect(() => {
    if (
      !isSdkInitialized ||
      !currentUserId ||
      typeof sdk?.groupChannel?.addGroupChannelHandler !== "function"
    ) {
      return undefined;
    }

    handlerIdRef.current = `inactive-tab-push-${uuidv4()}`;

    const handler = new GroupChannelHandler({
      onMessageReceived: (channel, message) => {
        notifyIncomingSendbirdMessage(channel, message, currentUserId);
      },
    });

    sdk.groupChannel.addGroupChannelHandler(handlerIdRef.current, handler);

    return () => {
      if (sdk?.groupChannel?.removeGroupChannelHandler && handlerIdRef.current) {
        sdk.groupChannel.removeGroupChannelHandler(handlerIdRef.current);
      }
    };
  }, [sdk, isSdkInitialized, currentUserId]);
};

export default useSendbirdDesktopNotifications;
